import styled, { createGlobalStyle, keyframes } from "styled-components";

export const GlobalStyle = createGlobalStyle`
  /* Equivalent to: body class="... bg-background-light dark:bg-background-dark ..." */
  body {
    margin: 0;
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
  }

  /* Simple dark mode toggle via class on <html> or <body>: "dark" */
  :root {
    --primary: #f3963f;
    --secondary: #EC6726;
    --accent-green: #B9BD5C;
    --neutral-dark: #343A40;
    --error-red: #DC3545;
    --background-light: #f8f7f5;
    --background-dark: #221910;

    --bg: var(--background-light);
    --text: var(--neutral-dark);
  }

  html.dark, body.dark {
    --bg: var(--background-dark);
    --text: var(--background-light);
  }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .6; }
`;

export const Main = styled.main`
  max-width: 42rem; /* max-w-2xl */
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 3rem; /* space-y-12 */
`;

export const Page = styled.div`
  /* min-height: 100vh; */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem; /* p-6 */
  position: relative;
  overflow: hidden;
`;

export const IllustrationContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

export const PulseBlob = styled.div`
  position: absolute;
  z-index: -10;
  width: 16rem; /* w-64 */
  height: 16rem;
  border-radius: 9999px;
  filter: blur(48px); /* blur-3xl-ish */
  animation: ${pulse} 2.4s ease-in-out infinite;
  background: rgba(243, 150, 63, 0.05);

  html.dark &,
  body.dark & {
    background: rgba(243, 150, 63, 0.1);
  }
`;

export const Group = styled.div`
  position: relative;

  &:hover .icon {
    transform: scale(1.05);
  }
`;

export const BigCircle = styled.div`
  width: 12rem; /* w-48 */
  height: 12rem; /* h-48 */
  border-radius: 9999px;
  margin-bottom: 2rem; /* mb-8 */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(243, 150, 63, 0.1);

  html.dark &,
  body.dark & {
    background: rgba(243, 150, 63, 0.2);
  }
`;

export const Icon = styled.span`
  font-size: 6rem; /* text-8xl */
  color: var(--secondary);
  transition: transform 300ms ease;
`;

export const ShadowLine = styled.div`
  width: 8rem; /* w-32 */
  height: 0.5rem; /* h-2 */
  border-radius: 9999px;
  margin: 0 auto;
  filter: blur(4px);
  background: rgba(52, 58, 64, 0.05);

  html.dark &,
  body.dark & {
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem; /* space-y-4 */
`;

export const Title = styled.h1`
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--neutral-dark);
  font-size: 2.25rem; /* text-4xl */

  html.dark &,
  body.dark & {
    color: #fff;
  }

  @media (min-width: 768px) {
    font-size: 3rem; /* md:text-5xl */
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 1.125rem; /* text-lg */
  line-height: 1.75;
  max-width: 32rem; /* max-w-lg */
  margin-left: auto;
  margin-right: auto;
  color: rgba(52, 58, 64, 0.7);

  html.dark &,
  body.dark & {
    color: rgba(248, 247, 245, 0.7);
  }

  @media (min-width: 768px) {
    font-size: 1.25rem; /* md:text-xl */
  }
`;

export const Actions = styled.div`
  padding-top: 1rem; /* pt-4 */
  display: flex;
  flex-direction: column;
  gap: 1rem; /* gap-4 */
  align-items: center;
  justify-content: center;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const GradientButton = styled.button`
  width: 100%;
  cursor: pointer;
  border: none;
  color: #fff;
  padding: 1rem 2rem; /* px-8 py-4 */
  border-radius: 0.5rem; /* rounded-lg */
  font-size: 1.125rem; /* text-lg */
  font-weight: 600;
  transition: all 200ms ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  background: linear-gradient(135deg, #f3963f);
  box-shadow: 0 10px 25px rgba(243, 150, 63, 0.2);

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: 640px) {
    width: auto;
  }
`;

export const OutlineLink = styled.a`
  width: 100%;
  text-decoration: none;
  border: 2px solid #f3963f;
  color: #f3963f;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  transition: all 200ms ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #EC6726;
    color: #fff;
  }

  @media (min-width: 640px) {
    width: auto;
  }
`;

export const MaterialIcon = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: 1.25rem; /* text-xl */
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;

  /* smooth */
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  -moz-osx-font-smoothing: grayscale;
`;

export const Secondary = styled.div`
  padding-top: 2rem; /* pt-8 */
  border-top: 1px solid rgba(243, 150, 63, 0.1);

  html.dark &,
  body.dark & {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

export const ErrorDetailsButton = styled.button`
  margin: 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  color: var(--error-red);
  font-weight: 500;
  font-size: 0.875rem;

  transition: color 160ms ease;

  &:hover {
    color: rgba(220, 53, 69, 0.8);
  }

  &:hover .chev {
    transform: translateX(4px);
  }
`;

export const Chev = styled(MaterialIcon)`
  transition: transform 200ms ease;
`;

export const RefId = styled.p`
  margin-top: 1rem; /* mt-4 */
  margin-bottom: 0;
  font-size: 0.75rem; /* text-xs */
  font-weight: 600;
  letter-spacing: 0.16em; /* tracking-widest */
  text-transform: uppercase;
  color: rgba(52, 58, 64, 0.4);

  html.dark &,
  body.dark & {
    color: rgba(248, 247, 245, 0.4);
  }
`;

export const DecoLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: -20;
`;

export const DecoBlob1 = styled.div`
  position: absolute;
  top: -10%;
  left: -10%;
  width: 40%;
  height: 40%;
  border-radius: 9999px;
  filter: blur(120px);
  background: rgba(243, 150, 63, 0.05);

  html.dark &,
  body.dark & {
    background: rgba(243, 150, 63, 0.05);
  }
`;

export const DecoBlob2 = styled.div`
  position: absolute;
  bottom: -10%;
  right: -10%;
  width: 30%;
  height: 30%;
  border-radius: 9999px;
  filter: blur(100px);
  background: rgba(185, 189, 92, 0.05);

  html.dark &,
  body.dark & {
    background: rgba(185, 189, 92, 0.05);
  }
`;

export const Hidden = styled.div`
  display: none;
`;